<template>
  <form v-if="isClient"
        class="bx-form bx-typo--form bx-form--search bx-form--search-epg bx-typo--form-search"
        :class="{ 'bx-js-filters-open': filtersOpen }">
    <StaticHeader v-if="config.hideFilters"
                  :headline="config.headline"
                  :short-link="config.shortLink" />
    <FilterHeader v-else
                  :filters-open="filtersOpen"
                  :selected-filters="selectedFilters"
                  @toggle-filters="handleFilterToggle()"
                  @update:model-value="airTimeOptionsDirectLink($event)" />

    <SelectedFilters :selected-filters="selectedFilters"
                     :filters="filters"
                     @remove-filter="removeFilter($event)" />

    <div class="bx-filters__wrapper">
      <div>
        <FilterSection title="Uhrzeit und Datum">
          <FilterItem v-model="selectedFilters.airTimeOptions"
                      :options="filters.airTimeOptions"
                      type="radio"
                      @update:model-value="selectedFilters.airTimeOptions=$event" />
          <CustomAirtimeFilter v-if="selectedFilters.airTimeOptions === 'custom'"
                               v-model="selectedFilters.customLocalTime" />
        </FilterSection>

        <FilterSection title="Sendungsart">
          <FilterItem v-model="selectedFilters.categoryAll"
                      :options="filters.categoryAll"
                      type="radio"
                      @update:model-value="selectedFilters.categoryIds=[]" />

          <FilterItem v-model="selectedFilters.categoryIds"
                      :options="filters.categoryIds"
                      type="checkbox"
                      @update:model-value="selectedFilters.categoryIds=$event;selectedFilters.categoryAll=false" />
        </FilterSection>

        <div>
          <FilterSection v-if="viewMode !== 'channel'"
                         title="Sender">
            <FilterItem v-model="selectedFilters.packageName"
                        :options="filters.packageName"
                        type="radio"
                        @update:model-value="selectedFilters.packageName=$event" />
          </FilterSection>

          <FilterSection title="Tagestipps">
            <label>
              {{ filters.isDailyTip[0].label }}
              <ToggleSwitch v-model="selectedFilters.isDailyTip" />
            </label>
          </FilterSection>
        </div>
      </div>
      <FilterActions @reset-filters="resetFilters"
                     @apply-filters="applyFilters" />
    </div>
    <ApplyOrDiscardDialog v-if="showApplyOrDiscardDialog"
                          @apply-filters="handleFilterToggleDialogEmit('apply')"
                          @discard-filters="handleFilterToggleDialogEmit('discard')"
                          @change-filters="handleFilterToggleDialogEmit('change')" />
  </form>
</template>

<script>
import { mapState } from 'pinia'

import { useConfigStore } from '../../../../../stores/config'
import clientOnly from '../../../../../mixins/client-only'
import ApplyOrDiscardDialog from './ApplyOrDiscardDialog.vue'
import FilterHeader from './Header.vue'
import StaticHeader from './StaticHeader.vue'
import FilterSection from './Section.vue'
import FilterItem from './Item.vue'
import CustomAirtimeFilter from './CustomAirtime.vue'
import SelectedFilters from './Selected.vue'
import FilterActions from './Actions.vue'
import ToggleSwitch from './ToggleSwitch.vue'

export default {
  components: {
    ApplyOrDiscardDialog,
    FilterSection,
    FilterItem,
    CustomAirtimeFilter,
    SelectedFilters,
    FilterActions,
    ToggleSwitch,
    FilterHeader,
    StaticHeader
  },
  mixins: [clientOnly],
  props: {
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    viewMode: {
      type: String,
      default: 'overview'
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update-filters', 'filters-ready'],
  data () {
    return {
      showApplyOrDiscardDialog: false,
      filtersReady: false,
      filtersOpen: false,
      filtersMemory: null, // Stores previous filter state for comparison
      selectedFilters: {} // Holds the currently selected filter options
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']),

    // Defines available filter options
    filters () {
      return {
        airTimeOptions: [
          { label: 'Jetzt im TV', value: 'now', default: true },
          { label: 'Heute um 20:15', value: 'today,20:15' },
          { label: 'Heute um 22:00', value: 'today,22:00' },
          { label: 'Morgen um 20:15', value: 'tomorrow,20:15' },
          { label: 'Morgen um 22:00', value: 'tomorrow,22:00' },
          { label: 'Eigene Uhrzeit & Datum', value: 'custom', selectedTagLabel: this.customAirtimeLabel }
        ],
        categoryAll: [{ label: 'Alle', value: true, default: true }],
        categoryIds: this.initFilters(this.rsConfig.epg.broadcastCategories),
        packageName: [{ label: 'Alle', value: 'all', default: true }, ...this.initFilters(this.rsConfig.epg.channelPackages)],
        isDailyTip: [
          { label: 'Nur Tagestipps anzeigen', value: true, selectedTagLabel: 'Nur Tagestipps' }
        ]
      }
    },

    // Determines if selected time is today, tomorrow, or a specific date
    airTimeDay () {
      if (!this.selectedFilters.airTime) return null

      const date = new Date(this.selectedFilters.airTime)
      const now = new Date()

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)

      const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

      if (selectedDate.getTime() === today.getTime()) {
        return 'today'
      } else if (selectedDate.getTime() === tomorrow.getTime()) {
        return 'tomorrow'
      }

      return date.toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit' })
    },

    // Returns selected time in HH:MM format
    airTimeTime () {
      if (!this.selectedFilters.airTime) return null

      const date = new Date(this.selectedFilters.airTime)
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    },

    // Returns label for custom airtime selection
    customAirtimeLabel () {
      if (!this.airTimeDay || !this.airTimeTime) return ''
      return `${this.airTimeDay === 'today' ? 'Heute' : this.airTimeDay === 'tomorrow' ? 'Morgen' : this.airTimeDay} um ${this.airTimeTime}`
    },

    // Returns the current time rounded to the next 5 minutes in ISO format
    airTimeNow () {
      const now = new Date()
      now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0) // round to next 5 minutes
      return now.toISOString()
    }
  },
  watch: {
    filtersOpen (newValue) {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      if (newValue && viewportWidth < 840) {
        // Scroll to top when filters are opened
        scroll(0, 0)
        document.body.classList.add('bx-js-dialog-opened')
      } else {
        document.body.classList.remove('bx-js-dialog-opened')
      }
    },
    'selectedFilters.airTimeOptions' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal && newVal !== 'custom') {
        this.selectedFilters.airTime = this.getAirTimeByAirTimeOption(newVal)
      }
    },
    'selectedFilters.airTime' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal) {
        this.selectedFilters.customLocalTime = this.formatLocalDateTime(newVal)
        this.selectedFilters.airTimeOptions = this.getAirTimeOption()
      }
    },
    'selectedFilters.customLocalTime' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal) {
        this.selectedFilters.airTime = new Date(newVal).toISOString()
      }
    }
  },
  beforeMount () {
    // initialize filters with default values
    this.resetFilters()
    // when the filters are loaded, we notify the epg search
    this.$emit('filters-ready', this.selectedFilters)
    this.filtersReady = true
  },
  methods: {
    /**
     * Updates the airtime filter based on the selected option.
     * @param {string} airTimeOptions - The selected airtime option.
     */
    airTimeOptionsDirectLink (airTimeOptions) {
      this.selectedFilters.airTimeOptions = airTimeOptions
      // give watchers time to recalculate the time depending values before we apply the filters
      this.$nextTick(() => {
        this.applyFilters()
      })
    },

    /**
     * Determines the correct filter label based on the selected airtime.
     */
    getAirTimeOption () {
      if (!this.airTimeDay || !this.airTimeTime) {
        return 'custom'
      }

      // Check if the airTime matches one of the predefined options
      const matchingOption = this.filters.airTimeOptions.find(option =>
        option.value === `${this.airTimeDay},${this.airTimeTime}`
      )

      // If a matching option is found, return it, otherwise fallback to "custom"
      return matchingOption ? matchingOption.value : this.selectedFilters.airTime === this.airTimeNow ? 'now' : 'custom'
    },

    /**
     * Toggles filter visibility and manages state backup.
     */
    handleFilterToggle () {
      if (!this.filtersOpen) {
        // filter is closed and we want to open it
        // make copy of actually selected filters
        this.filtersMemory = JSON.stringify(this.selectedFilters)
      } else {
        // filter is opened and we want to close it
        // before close, check if any filters were changed and also saved
        const filtersString = JSON.stringify(this.selectedFilters)
        if (this.filtersMemory !== filtersString) {
          this.showApplyOrDiscardDialog = true
          return
        }
      }
      this.filtersOpen = !this.filtersOpen
    },

    /**
     * Handles the emission of filter toggle dialog actions.
     * @param {string} action - The action to perform ('apply', 'discard', 'change').
     */
    handleFilterToggleDialogEmit (action) {
      if (action === 'apply') {
        this.applyFilters()
      } else if (action === 'discard') {
        // reset filters to the last saved state
        this.selectedFilters = JSON.parse(this.filtersMemory)
        this.filtersOpen = false
      }
      this.showApplyOrDiscardDialog = false
    },

    /**
     * Initializes filter options with labels.
     * @param {Array} filterData - The filter data to initialize.
     * @param {string} labelPrefix - Optional prefix for labels.
     * @returns {Array} The initialized filter options.
     */
    initFilters (filterData) {
      return filterData.map(item => {
        const key = Object.keys(item)[0]
        return { value: key, label: item[key] }
      })
    },

    /**
     * Resets all filters to their initial state.
     */
    resetFilters () {
      // initial filters are not defining airTimeOptions and customLocalTime
      // so we need to set them to default values if airTime is predefined
      // and if not, we set them to default values
      //
      // after initializing the filters, the three corresponding values will be
      // updated by the watchers if one of the values is changed
      let initAirTime = this.airTimeNow
      let initAirTimeOptions = 'now'
      let initCustomLocalTime = this.formatLocalDateTime(this.airTimeNow)

      if (this.initialFilters.airTime) {
        initAirTime = this.initialFilters.airTime
        initAirTimeOptions = this.getAirTimeOption()
        initCustomLocalTime = this.formatLocalDateTime(this.initialFilters.airTime)
      }

      this.selectedFilters = {
        airTime: initAirTime,
        airTimeOptions: initAirTimeOptions,
        customLocalTime: initCustomLocalTime,
        categoryAll: true,
        categoryIds: [],
        packageName: 'all',
        isDailyTip: false,
        offset: 0,
        limit: 10,
        ...this.initialFilters // merge initialFilters with default values
      }
    },

    /**
     * Applies the selected filters and emits an event to update the parent component.
     */
    applyFilters () {
      this.$emit('update-filters', this.selectedFilters)
      this.filtersOpen = false
    },

    /**
     * Removes a specific filter based on its name and value.
     * @param {string} filter.filterName - The name of the filter to remove.
     * @param {string} filter.value - The value of the filter to remove.
     */
    removeFilter ({ filterName, value }) {
      if (filterName === 'categoryIds') {
        this.selectedFilters.categoryIds = this.selectedFilters.categoryIds.filter(item => item !== value)
        if (this.selectedFilters.categoryIds.length === 0) {
          this.selectedFilters.categoryAll = true
        }
      } else if (filterName === 'packageName') {
        this.selectedFilters.packageName = 'all'
      } else if (filterName === 'isDailyTip') {
        this.selectedFilters.isDailyTip = false
      } else if (filterName === 'airTimeOptions') {
        this.selectedFilters.airTimeOptions = 'now'
        this.selectedFilters.airTime = this.airTimeNow
      }
      this.$emit('update-filters', this.selectedFilters)
    },

    /**
     * Converts a given airtime option into a JavaScript Date object.
     * @param {string} airTimeOption - The airtime option to convert.
     * @returns {string} The converted airtime in ISO format.
     */
    getAirTimeByAirTimeOption (airTimeOption) {
      const now = new Date()
      now.setSeconds(0, 0) // set seconds & milliseconds to 0

      // Split option into date part and optional time part
      const [datePart, timePart] = airTimeOption.split(',')

      switch (datePart) {
        case 'now':
          return this.airTimeNow
        case 'today':
        case 'tomorrow':
          if (datePart === 'tomorrow') {
            now.setDate(now.getDate() + 1) // move to next day
          }
          if (timePart) {
            const [hours, minutes] = timePart.split(':').map(Number)
            now.setHours(hours, minutes)
          }
          break
        default:
          return this.selectedFilters.airTime // custom does not need any changes
      }

      return now.toISOString()
    },

    /**
     * Formats a JavaScript Date object into a `datetime-local` compatible string.
     *
     * @param {Date} date - The JavaScript Date object to format.
     * @returns {string} A string formatted as `YYYY-MM-DDTHH:MM`, compatible with HTML <input type="datetime-local">.
     *
     * Example:
     * ```js
     * const date = new Date('2025-04-02T14:30:00Z')
     * console.log(formatLocalDateTime(date)) // "2025-04-02T16:30" (if UTC+2)
     * ```
     */
    formatLocalDateTime (date) {
      // ensure correct type
      if (typeof date === 'string') {
        date = new Date(date)
      }
      // Ensures that numbers (month, day, hour, minute) are always two digits (e.g., "01" instead of "1").
      const pad = (num) => num.toString().padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>
